import { useEffect, useState } from "react";
import api, { money, fmtDate } from "@/lib/api";
import { useActivity } from "@/context/ActivityContext";
import Pagination from "@/components/Pagination";
import { Ban, ShoppingCart, HandCoins, ShieldCheck, User } from "lucide-react";

export default function Cancellations() {
  const { current } = useActivity();
  const [days, setDays] = useState(30);
  const [voider, setVoider] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], voiders: [], total_sales: 0, total_payments: 0, amount_sales: 0, amount_payments: 0 });

  const load = async () => {
    if (!current) return;
    const r = await api.get(`/cancellations?activity_id=${current.id}&days=${days}`);
    setData(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current, days]);

  if (!current) return <div className="text-[#64748B]">Sélectionnez une activité.</div>;

  const items = voider ? data.items.filter((i) => i.voided_by_id === voider) : data.items;
  const paged = items.slice((page - 1) * 10, page * 10);

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-2xl p-6 text-white shadow-lg" style={{ background: "linear-gradient(160deg,#1A3A5C 0%,#16324F 55%,#0E2135 100%)" }}>
        <div className="pointer-events-none absolute -right-16 -top-16 w-52 h-52 rounded-full bg-white/5 blur-2xl" />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-white/70"><ShieldCheck className="w-4 h-4" /> Contrôle</div>
            <h1 className="text-2xl md:text-3xl font-bold font-head mt-1.5">Journal des annulations</h1>
            <p className="text-sm text-white/75 mt-1">Ventes & encaissements annulés · {current.name}</p>
          </div>
          <div className="flex gap-2">
            <select className="rounded-xl bg-white/15 border border-white/25 text-white text-sm px-3 py-2 outline-none backdrop-blur focus:bg-white/25 [&>option]:text-[#0F172A]" value={voider} onChange={(e) => { setVoider(e.target.value); setPage(1); }} data-testid="cancel-voider-filter">
              <option value="">Tous les auteurs</option>
              {data.voiders.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
            </select>
            <select className="rounded-xl bg-white/15 border border-white/25 text-white text-sm px-3 py-2 outline-none backdrop-blur focus:bg-white/25 [&>option]:text-[#0F172A]" value={days} onChange={(e) => { setDays(Number(e.target.value)); setPage(1); }} data-testid="cancel-days-filter">
              <option value={7}>7 jours</option><option value={30}>30 jours</option><option value={90}>90 jours</option><option value={365}>1 an</option>
            </select>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-2xl border border-[#FECACA] bg-[#FEF2F2] p-4" data-testid="cancel-kpi-sales">
          <div className="flex items-center justify-between"><span className="kpi-label text-[#B91C1C]">Ventes annulées</span><span className="grid place-items-center w-9 h-9 rounded-xl bg-white shadow-sm"><ShoppingCart className="w-5 h-5 text-[#B91C1C]" /></span></div>
          <div className="mt-2 text-2xl font-extrabold font-head text-[#0F172A]">{data.total_sales} <span className="text-sm font-semibold text-[#64748B]">· {money(data.amount_sales)}</span></div>
        </div>
        <div className="rounded-2xl border border-[#FDE68A] bg-[#FFFBEB] p-4" data-testid="cancel-kpi-payments">
          <div className="flex items-center justify-between"><span className="kpi-label text-[#B45309]">Encaissements annulés</span><span className="grid place-items-center w-9 h-9 rounded-xl bg-white shadow-sm"><HandCoins className="w-5 h-5 text-[#B45309]" /></span></div>
          <div className="mt-2 text-2xl font-extrabold font-head text-[#0F172A]">{data.total_payments} <span className="text-sm font-semibold text-[#64748B]">· {money(data.amount_payments)}</span></div>
        </div>
      </div>

      <div className="card">
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full"><thead><tr>
            <th className="th">Type</th><th className="th">N° / Réf.</th><th className="th">Client</th>
            <th className="th text-right">Montant</th><th className="th">Annulé le</th><th className="th">Par</th><th className="th">Motif</th>
          </tr></thead><tbody>
            {paged.map((i, k) => (
              <tr key={k} className="row" data-testid={`cancel-row-${k}`}>
                <td className="td">{i.type === "sale" ? <span className="chip-blue">Vente</span> : <span className="chip-amber">Encaissement</span>}</td>
                <td className="td font-mono text-xs">{i.number}</td>
                <td className="td font-medium text-[#0F172A]">{i.customer_name}</td>
                <td className="td text-right font-semibold text-[#B91C1C]">{money(i.amount)}</td>
                <td className="td text-xs">{fmtDate(i.voided_at)}</td>
                <td className="td text-xs font-medium">{i.voided_by || "—"}</td>
                <td className="td text-xs text-[#64748B] max-w-[220px] truncate" title={i.voided_reason || ""}>{i.voided_reason || "—"}</td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="td text-center py-10 text-[#94A3B8]">Aucune annulation sur la période</td></tr>}
          </tbody></table>
        </div>

        <div className="md:hidden divide-y divide-[#E2E8F0]">
          {paged.map((i, k) => (
            <div key={k} className="py-4 first:pt-1" data-testid={`cancel-card-${k}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    {i.type === "sale" ? <span className="chip-blue">Vente</span> : <span className="chip-amber">Encaissement</span>}
                    <span className="font-mono text-xs text-[#94A3B8]">{i.number}</span>
                  </div>
                  <div className="font-semibold text-[#0F172A] mt-1 truncate">{i.customer_name}</div>
                </div>
                <span className="text-lg font-bold text-[#B91C1C] shrink-0">{money(i.amount)}</span>
              </div>
              <div className="mt-2 text-xs text-[#64748B] flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{i.voided_by || "—"} · {fmtDate(i.voided_at)}</div>
              {i.voided_reason && <div className="mt-1 text-xs text-[#64748B]">Motif : {i.voided_reason}</div>}
            </div>
          ))}
          {items.length === 0 && <div className="text-center py-10 text-[#94A3B8]">Aucune annulation sur la période</div>}
        </div>
        <Pagination page={page} setPage={setPage} total={items.length} />
      </div>
    </div>
  );
}
